import { FC } from 'react'

import { Widget } from '@factorialco/f0-react/dist/experimental'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ExpensesInsightDocument, ExpensesInsightQuery } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { buildNoQueryWidget, buildWidget } from 'modules/core/lib/widgets'

import ExpenseAmountByStatusTable from 'modules/expenses/pages/ExpensesReportsPage/ExpenseAmountByStatusTable'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

const DEFAULT_CURRENCY = 'eur'

const prepareData = (data: ExpensesInsightQuery) => {
  const currentMonth = moment()
    .format('MMMM')
    .replace(/^\w/, (c) => c.toLocaleUpperCase())

  const currency = data.employees.employee?.legalEntity?.currency ?? DEFAULT_CURRENCY
  return {
    subtitle: currentMonth,
    currency: currency,
    currentPeriod: data.expenses.currentPeriod.nodes[0],
  }
}
type PreparedData = ReturnType<typeof prepareData>

const ExpensesWidgetComponent: FC<{
  data: PreparedData
  employeeId: number
}> = ({
  data: { subtitle: subtitle, currency: currency, currentPeriod: currentPeriod },
  employeeId,
}) => {
  const { navigation } = useNavigation()
  const { employee: currentEmployee } = useCurrentSession()
  const isCurrentEmployee = employeeId === currentEmployee?.id

  const getExpensesLinkUrl = (isCurrentEmployee: boolean): string | null => {
    const baseUrl = isCurrentEmployee
      ? navigation.myExpenses.list.expenses.fullPath()
      : navigation.expenses.list.expenses.fullPath()
    const searchParams = new URLSearchParams()
    const today = i18nDateToString(moment(), 'backend')
    const firstDayOfCurrentMonth = i18nDateToString(moment().startOf('month'), 'backend')

    if (!isCurrentEmployee) {
      searchParams.append('employeeIds', [employeeId].join(','))
    }

    searchParams.append('startDate', firstDayOfCurrentMonth)
    searchParams.append('endDate', today)

    return `${baseUrl}?${searchParams.toString()}`
  }

  const linkUrl = getExpensesLinkUrl(isCurrentEmployee)
  const title = isCurrentEmployee
    ? i18n.t('insights.widgets.expenses.my_title')
    : i18n.t('insights.widgets.expenses.title')

  return (
    <Widget
      header={{
        title,
        subtitle: subtitle,
        link: linkUrl ? { title: title, url: linkUrl } : undefined,
      }}
    >
      <ExpenseAmountByStatusTable currentPeriod={currentPeriod} currency={currency} />
    </Widget>
  )
}

const ExpensesUpsellWidgetComponent: FC = () => {
  return (
    <Widget header={{ title: i18n.t('insights.widgets.expenses.title') }}>
      <UpsellingKit.EmptyState
        moduleRequested={AvailableModules.expenses}
        requestSource={'Profile insights upsell state'}
        featureRequested={Features.expenses_core}
        requestReason={ValidReasons.MissingFeature}
        title={i18n.t('insights.widgets.expenses.upsell.title')}
        description={i18n.t('insights.widgets.expenses.upsell.description')}
        variant="card"
      />
    </Widget>
  )
}

export const buildExpensesWidget = () =>
  buildWidget({
    id: 'expenses',
    document: ExpensesInsightDocument,
    isEmpty: (data) => !data.currentPeriod,
    prepareData,
    component: ExpensesWidgetComponent,
  })

export const buildExpensesUpsellWidget = () =>
  buildNoQueryWidget({
    id: 'expenses-upsell',
    component: ExpensesUpsellWidgetComponent,
  })
